import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { getSportTypeConfig } from '../../sports/configs';
import { getSweepStatusInfo } from '../../helpers/sweepStatusHelpers';
// eslint-disable-next-line import/no-named-as-default
import CountdownTimer from '../CountdownTimer';
import Winner from '../Winner';
import Entry from '../Entry';
import { TermsLinkWrapper } from '../LinkWrappers';
import {
    getFormattedDate,
    getFormattedTime
} from '../../helpers/dateTimeHelpers';
import * as Styled from './styles';

export const SweepCard = ({ sweep, entry }) => {
    const sportConfig = getSportTypeConfig(sweep.sportType);
    const { SportIconComponent } = sportConfig.icons;

    const hasEntered = entry ? entry.hasEntered : false;
    const { label, showCountdown, cta } = getSweepStatusInfo(sweep, hasEntered);
    const backgroundImage = sweep.assets?.backgroundUrl;

    // Guard downstream usage so a truthy-but-invalid date can't surface.
    const closeMs = sweep.closeDate ? new Date(sweep.closeDate).getTime() : NaN;
    const hasValidCloseDate = !Number.isNaN(closeMs);

    const entriesTime = hasValidCloseDate
        ? getFormattedTime(sweep.closeDate)
        : null;
    const entriesDate = hasValidCloseDate
        ? getFormattedDate(sweep.closeDate, 'DD.MM.YYYY')
        : null;
    const entriesUntil =
        entriesTime && entriesDate
            ? `Entries until ${entriesTime} ${entriesDate}. `
            : null;

    return (
        <Styled.CardCell>
            <Styled.Card>
                <Styled.CardBackground
                    sport={sportConfig.type}
                    backgroundUrl={backgroundImage}
                >
                    <Styled.SportIcon>
                        {SportIconComponent && <SportIconComponent />}
                    </Styled.SportIcon>
                    <Styled.StatusBadge>{label}</Styled.StatusBadge>
                </Styled.CardBackground>
                <Styled.CardBodyWrapper>
                    {sweep.winnerName ? (
                        <Winner
                            winnerName={sweep.winnerName}
                            winnerLocation={sweep.winnerLocation || undefined}
                        />
                    ) : (
                        <>
                            <Styled.CountdownWrapper>
                                <Styled.SweepName>
                                    {sweep.name?.toUpperCase()}
                                </Styled.SweepName>
                                {showCountdown && hasValidCloseDate && (
                                    <CountdownTimer
                                        date={closeMs}
                                        name=""
                                        showDateHeader={false}
                                        variant="compact"
                                    />
                                )}
                            </Styled.CountdownWrapper>
                            {hasEntered && (
                                <Styled.SelectionWrapper>
                                    <Entry
                                        sportType={sweep.sportType}
                                        selectionName={entry?.selection?.name}
                                        selectionIcon={
                                            entry?.selection?.metaData?.silk
                                        }
                                        selectionNumber={
                                            entry?.selection?.metaData
                                                ?.clothNumber
                                        }
                                    />
                                </Styled.SelectionWrapper>
                            )}
                        </>
                    )}
                    <Styled.CardFooter>
                        <Link
                            to={`/sweep/${sweep.sweepId}${cta.suffix}`}
                            style={{ width: '100%', textDecoration: 'none' }}
                        >
                            <Styled.CtaButton>{cta.text}</Styled.CtaButton>
                        </Link>
                    </Styled.CardFooter>
                </Styled.CardBodyWrapper>
            </Styled.Card>
            <Styled.Terms>
                {entriesUntil}
                <TermsLinkWrapper>
                    <Styled.TermsLink>
                        18+, T&amp;Cs apply, see here.
                    </Styled.TermsLink>
                </TermsLinkWrapper>
            </Styled.Terms>
        </Styled.CardCell>
    );
};

SweepCard.defaultProps = {
    entry: undefined
};

SweepCard.propTypes = {
    sweep: PropTypes.shape({
        sweepId: PropTypes.string.isRequired,
        name: PropTypes.string,
        assets: PropTypes.shape({
            backgroundUrl: PropTypes.string,
            prizeUrl: PropTypes.string
        }),
        status: PropTypes.string,
        sportType: PropTypes.string,
        closeDate: PropTypes.string,
        winnerName: PropTypes.string,
        winnerLocation: PropTypes.string,
        event: PropTypes.shape({
            name: PropTypes.string
        })
    }).isRequired,
    entry: PropTypes.shape({
        hasEntered: PropTypes.bool,
        selection: PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            metaData: PropTypes.shape({
                clothNumber: PropTypes.string,
                jockeyName: PropTypes.string,
                trainerName: PropTypes.string,
                silk: PropTypes.string
            })
        })
    })
};

export default SweepCard;
